import type { ReactNode } from 'react';

import { XIcon } from 'lucide-react';
import { match, P } from 'ts-pattern';

import { Button } from '@nesso/ui/primitives/button';
import { Card, CardContent } from '@nesso/ui/primitives/card';
import { Popover, PopoverAnchor, PopoverContent } from '@nesso/ui/primitives/popover';

import { useNoteCopyIntro } from '../../model/use-note-copy-intro';

type Props = {
    sessionId: string;
    children: ReactNode;
};

export function CopySessionNoteIntroPopover({ sessionId, children }: Props) {
    const { isOpen, noteText, onDismiss } = useNoteCopyIntro(sessionId);

    return match({ isOpen, noteText })
        .with({ isOpen: true, noteText: P.nonNullable }, ({ noteText }) => (
            <Popover open onOpenChange={(open) => !open && onDismiss()}>
                <PopoverAnchor>{children}</PopoverAnchor>
                <PopoverContent
                    side="top"
                    align="end"
                    sideOffset={12}
                    onInteractOutside={(e) => e.preventDefault()}
                    className="w-lg max-w-[calc(100vw-2rem)] p-6 flex flex-col gap-4"
                >
                    <div className="flex flex-col gap-1.5">
                        <p className="text-lg leading-none font-semibold text-foreground">
                            Review your note and copy it to your EHR
                        </p>
                        <p className="text-base leading-normal text-muted-foreground">
                            Here&rsquo;s the unedited version of your note for reference
                        </p>
                    </div>
                    <Card className="bg-muted/40">
                        <CardContent
                            className="max-h-80 overflow-y-auto overscroll-contain"
                            onWheel={(e) => e.stopPropagation()}
                        >
                            <pre className="font-sans text-sm text-foreground whitespace-pre-wrap">
                                {noteText}
                            </pre>
                        </CardContent>
                    </Card>
                    <div className="flex justify-end">
                        <Button onClick={onDismiss}>Got it</Button>
                    </div>
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={onDismiss}
                        className="absolute top-4 right-4 rounded-xs opacity-70 hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    >
                        <XIcon className="size-4" />
                    </button>
                </PopoverContent>
            </Popover>
        ))
        .otherwise(() => <>{children}</>);
}
